import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { recordPageVisit } from '../services/pageVisitsApi';
import { resolvePageVisitDefinition } from '../constants/pageVisits';

const isDocumentVisible = () => {
  if (typeof document === 'undefined') return true;
  return !document.hidden;
};

export function usePageVisitTracker({ user, enabled = true } = {}) {
  const location = useLocation();
  const lastTrackedSignatureRef = useRef('');
  const activeVisitRef = useRef(null);

  const accumulateVisibleTime = useCallback(() => {
    if (typeof document === 'undefined') return;
    const visit = activeVisitRef.current;
    if (!visit) return;
    if (document.hidden) {
      if (visit.visibleSince) {
        visit.accumulatedMs += Date.now() - visit.visibleSince;
        visit.visibleSince = null;
      }
    } else {
      visit.visibleSince = Date.now();
    }
  }, []);

  const finalizeActiveVisit = useCallback(
    (reason = 'route-change') => {
      const visit = activeVisitRef.current;
      if (!visit || !visit.pageKey) {
        return;
      }

      accumulateVisibleTime();

      const { pageKey, path, label, userSnapshot, startedAt, accumulatedMs, visibleSince } = visit;
      activeVisitRef.current = null;

      const now = Date.now();
      let durationMs = accumulatedMs || 0;
      if (visibleSince) {
        durationMs += now - visibleSince;
      }
      if (durationMs <= 0 && startedAt) {
        durationMs = Math.max(now - startedAt, 0);
      }

      recordPageVisit({
        pageKey,
        path,
        label,
        user: userSnapshot,
        durationMs,
      });
    },
    [accumulateVisibleTime]
  );

  useEffect(() => {
    if (!enabled) {
      finalizeActiveVisit('disabled');
      lastTrackedSignatureRef.current = '';
      return;
    }

    const pathname = location?.pathname || '';
    if (!pathname) {
      finalizeActiveVisit('missing-path');
      lastTrackedSignatureRef.current = '';
      return;
    }

    const search = location?.search || '';
    const locationKey = location?.key || '';
    const visitSignature = `${locationKey}:${pathname}${search}`;

    const definition = resolvePageVisitDefinition(pathname, search);
    if (!definition) {
      finalizeActiveVisit('no-definition');
      lastTrackedSignatureRef.current = '';
      return;
    }

    if (visitSignature === lastTrackedSignatureRef.current && activeVisitRef.current) {
      return;
    }

    lastTrackedSignatureRef.current = visitSignature;
    finalizeActiveVisit('route-change');

    const label = definition.label || pathname;
    const pageKey = definition.key;
    const path = `${pathname}${search}`;
    const now = Date.now();
    const visible = isDocumentVisible();

    activeVisitRef.current = {
      pageKey,
      path,
      label,
      userSnapshot: user,
      startedAt: now,
      accumulatedMs: 0,
      visibleSince: visible ? now : null,
    };
  }, [location, user, enabled, finalizeActiveVisit]);

  useEffect(() => {
    if (!enabled) {
      return () => undefined;
    }

    const handleVisibilityChange = () => {
      accumulateVisibleTime();
    };

    const handlePageHide = () => {
      finalizeActiveVisit('pagehide');
    };

    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', handleVisibilityChange);
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('pagehide', handlePageHide);
      window.addEventListener('beforeunload', handlePageHide);
    }

    return () => {
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      }
      if (typeof window !== 'undefined') {
        window.removeEventListener('pagehide', handlePageHide);
        window.removeEventListener('beforeunload', handlePageHide);
      }
      finalizeActiveVisit('unmount');
    };
  }, [enabled, accumulateVisibleTime, finalizeActiveVisit]);

  useEffect(() => {
    return () => {
      finalizeActiveVisit('cleanup');
    };
  }, [finalizeActiveVisit]);
}
